import { createActionError } from '@/workflow-builder/_pure/model/error';
import { failExecution, isCurrentExecution } from '@/workflow-builder/_pure/model/executionState';
import type { PrimitiveExecutionSnapshot, PrimitiveWorkflowSession } from '@/workflowExecution/_contract/types';
import type { WorkflowRuntimeState } from '@/workflow/_contract/runtime';
import type { Config } from '@/workflow-builder/_contract/config';
import type { ExecutionRequest, RuntimeInputSubmission, RuntimeState } from '@/workflow-builder/_contract/runtime';
import type { RunWorkflowCapabilities } from '@/workflow-builder/_contract/runWorkflow';

export function createExecutionOwner(capabilities: RunWorkflowCapabilities) {
    const started = new WeakSet<ExecutionRequest>();
    const sessions = new WeakMap<ExecutionRequest, PrimitiveWorkflowSession>();
    const submitted = new WeakSet<RuntimeInputSubmission>();
    return (args: { config: Config; runtimeState: RuntimeState }) => {
        const execution = args.runtimeState.executionState;
        if (execution.executionStateKind !== 'running' || !execution.request) return;
        const request = execution.request;
        const requestId = execution.requestId;
        const updateRuntimeState = args.config.runtimeCallbacks?.onRuntimeStateChange;
        const { createLocalSession, executeWorkflow, handoffToRunner } = capabilities;
        if (!updateRuntimeState || !handoffToRunner) {
            throw new Error('Committed Builder execution requires runtime callbacks.');
        }
        const isCurrent = (state: unknown): state is RuntimeState =>
            isCurrentExecution(state, requestId)
            && state.executionState.executionStateKind === 'running'
            && state.executionState.request === request
            && (request.executionMode !== 'local' || state.executionState.localInput === execution.localInput);
        const fail = (message: string) => updateRuntimeState((state: WorkflowRuntimeState) => isCurrent(state)
            ? failExecution(state, requestId, createActionError('run_workflow', message)) : state);
        const publishLocal = (snapshot: PrimitiveExecutionSnapshot) => {
            if (snapshot.status === 'failed') {
                sessions.delete(request);
                fail(snapshot.error.message);
            } else if (snapshot.status === 'completed') {
                sessions.delete(request);
                handoffToRunner({
                    workflowTraceHandle: snapshot.trace.handle, workflowTrace: snapshot.trace,
                    updateRuntimeState, isCurrentRuntimeState: isCurrent,
                });
            } else if (snapshot.status === 'waiting') {
                updateRuntimeState((state: WorkflowRuntimeState) => isCurrent(state) ? {
                    ...state, error: null, executionState: {
                        executionStateKind: 'running', requestId, request,
                        localInput: { requirement: snapshot.requirement, text: '' },
                    },
                } : state);
            } else {
                throw new Error('Local execution did not advance to a waiting or terminal state.');
            }
        };
        if (request.executionMode === 'local') {
            const submission = execution.localInput?.submission;
            if (started.has(request) && (!submission || submitted.has(submission))) return;
            try {
                if (submission) {
                    submitted.add(submission);
                    const session = sessions.get(request);
                    if (!session) throw new Error('Local workflow session is no longer available. Start a new run.');
                    publishLocal(session.provideInput(submission));
                } else {
                    started.add(request);
                    if (!createLocalSession) throw new Error('Local workflow execution is unavailable.');
                    const session = createLocalSession({ workflow: request.workflow });
                    sessions.set(request, session);
                    publishLocal(session.run());
                }
            } catch (error: unknown) {
                sessions.delete(request);
                fail(error instanceof Error ? error.message : String(error));
            }
            return;
        }
        if (started.has(request)) return;
        started.add(request);
        if (!executeWorkflow) {
            fail('Remote workflow execution is unavailable.');
            return;
        }
        void (async () => executeWorkflow(request.workflow, {
            writeLocalWorkflowTrace: request.writeLocalWorkflowTrace,
        }))().then((result) => {
            if (!result.ok) {
                fail(result.error);
                return;
            }
            handoffToRunner({
                workflowTraceHandle: result.workflowTraceHandle,
                updateRuntimeState,
                isCurrentRuntimeState: isCurrent,
            });
        }).catch((error: unknown) => {
            fail(error instanceof Error ? error.message : 'Run graph callback failed.');
        });
    };
}
